import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  clearAdminSession,
  getAdminPermissions,
  getAdminRole,
  getAdminToken,
  getAdminUser,
  saveAdminSession,
} from '@formosa/shared/auth/tokenStorage';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => getAdminToken());
  const [user, setUser] = useState(() => getAdminUser());
  const [permissions, setPermissions] = useState(() => getAdminPermissions());
  const [role, setRole] = useState(() => getAdminRole());

  const loginSuccess = useCallback((data) => {
    const { authToken, admin, permissions: perms, role: roleData } = data || {};
    saveAdminSession({
      authToken,
      admin,
      permissions: perms,
      role: roleData,
    });
    setToken(authToken);
    setUser(admin || null);
    setPermissions(perms || []);
    setRole(roleData || null);
  }, []);

  const logout = useCallback(() => {
    clearAdminSession();
    setToken(null);
    setUser(null);
    setPermissions([]);
    setRole(null);
  }, []);

  useEffect(() => {
    const handleExpired = () => logout();
    window.addEventListener('formosa:admin-auth-expired', handleExpired);
    return () =>
      window.removeEventListener('formosa:admin-auth-expired', handleExpired);
  }, [logout]);

  const value = useMemo(
    () => ({
      token,
      user,
      role,
      permissions,
      isAuthenticated: Boolean(token),
      loginSuccess,
      logout,
    }),
    [token, user, role, permissions, loginSuccess, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth 必須在 AuthProvider 內使用');
  }
  return context;
}
